// Central authenticated API helper for SelfieBooth backend requests.
//
// Wraps the browser's native fetch with the same base-URL convention every
// service file already used (REACT_APP_API_BASE_URL, defaulting to
// https://api.dealzta.com/api/selfiebooth), and attaches the Dealzta access token (stored
// by authService.js's login()) as a Bearer token whenever one is present.
// This talks to the SelfieBooth backend only — Dealzta's own endpoints are
// still called directly by authService.js.

import { getStoredToken, logout, refreshAccessToken, SESSION_EXPIRED } from './authService';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.dealzta.com/api/selfiebooth';

// Dispatched on `window` once a SelfieBooth API request is still 401 after
// an access-token refresh was attempted (i.e. the Dealzta session itself is
// gone), after the stored Dealzta auth has already been cleared. App.js
// listens for this to reset its view back to Login without this module
// needing to know anything about React/view state.
export const UNAUTHENTICATED_EVENT = 'dealzta:unauthenticated';

function sendRequest(path, options, token) {
  const headers = {
    Accept: 'application/json',
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return fetch(`${API_BASE_URL}${path}`, { ...options, headers });
}

function signalUnauthenticated() {
  logout();
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
  }
}

/**
 * fetch() against the SelfieBooth backend. Attaches `Authorization: Bearer
 * <token>` when a Dealzta access token is stored — callers decide whether
 * their endpoint actually requires it, this never forces a login.
 *
 * On a 401 the access token is treated as expired, not the session: it is
 * refreshed via authService.refreshAccessToken() (one shared refresh for
 * all concurrent 401s) and the request is retried exactly ONCE with the new
 * token. Only if the refresh is rejected, or the retry is still 401, is the
 * stored Dealzta auth cleared and UNAUTHENTICATED_EVENT dispatched. If
 * Dealzta is merely unreachable during the refresh, auth is kept and the
 * original 401 Response is returned. The Response is always returned so
 * the caller can handle/report it as needed. Never logs the token.
 */
export async function apiFetch(path, options = {}) {
  const token = getStoredToken();
  const response = await sendRequest(path, options, token);

  if (response.status !== 401) {
    return response;
  }

  let newToken;
  const currentToken = getStoredToken();
  if (currentToken && currentToken !== token) {
    // Another request already refreshed while this one was in flight —
    // just retry with that token instead of starting a second refresh.
    newToken = currentToken;
  } else {
    try {
      newToken = await refreshAccessToken();
    } catch (err) {
      if (err.code === SESSION_EXPIRED) {
        signalUnauthenticated();
      }
      return response;
    }
  }

  const retryResponse = await sendRequest(path, options, newToken);

  if (retryResponse.status === 401) {
    signalUnauthenticated();
  }

  return retryResponse;
}

/**
 * apiFetch + the success/data JSON-envelope check every service file
 * already duplicated. Resolves to body.data (may be an empty array/object),
 * throws on a genuine network failure, non-2xx response, or a
 * `{ success: false }` envelope.
 */
export async function fetchJson(path, options = {}) {
  const response = await apiFetch(path, options);
  const body = await response.json();

  if (!response.ok || !body.success) {
    throw new Error(body.message || `Unable to fetch ${path}`);
  }

  return body.data;
}
